<template>
  <ui-dialog :show="show" @input="closeDialog">
    <template #header>
      <div
        class="d-flex flex-row align-center justify-space-between flex-grow-1"
      >
        <h3>
          {{
            editMode
              ? $t("message.permissions.editUserPermissions")
              : $t("message.permissions.userPermissions")
          }}
        </h3>
        <v-btn
          icon
          @click="showEditPermissions"
          v-if="previewMode && member.type === accountTypes.MANAGER"
        >
          <v-icon size="24">icon-edit</v-icon>
        </v-btn>
      </div>
    </template>

    <PreviewData show-type show-email show-count divider />

    <div class="permissions-list">
      <div
        class="permissions-list-group"
        v-for="group in filteredGroups"
        :key="group.name"
      >
        <div class="right-label" v-if="group.rightLabel">
          {{ $t("message.permissions.access") }}
        </div>

        <div class="name">
          <span class="mr-2">{{ group.name }}</span>
          <TooltipIcon
            color="accent"
            icon="mdi-information"
            :icon-size="20"
            v-if="group.tooltip"
          >
            {{ group.tooltip }}
          </TooltipIcon>
        </div>

        <div class="group-permissions">
          <PermissionItem
            v-for="item in group.children"
            :key="item.name"
            :name="item.name"
            :related-permission="item.relatedPermission"
            :readonly="previewMode || item.readonly"
          />
        </div>
      </div>
    </div>

    <SectionLoader :status="loading" :opacity="1" />

    <template #actions>
      <template v-if="!previewMode">
        <ui-button @click="showFacilitiesDialog" v-if="!editMode">
          {{ $t("message.common.back") }}
        </ui-button>
        <ui-button
          color="primary"
          @click="showCompleteDialog"
          :class="{ 'ml-auto': editMode }"
        >
          {{
            editMode
              ? $t("message.common.save")
              : $t("message.permissions.addUser")
          }}
        </ui-button>
      </template>
    </template>
  </ui-dialog>
</template>

<script>
import { PermissionsActiveDialog } from "@/enums/permissions-active-dialog";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { companyMember } from "@/helpers/mixins/company-member";
import { snakeCase } from "lodash";
import { FacilityPermissionsEnum } from "@/enums/facility-permissions.enum";

import PreviewData from "@/components/Permissions/PreviewData";
import TooltipIcon from "@/components/ui/TooltipIcon";
import PermissionItem from "@/components/Permissions/PermissionItem";
import SectionLoader from "@/components/Loader/SectionLoader";

import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { RELATION } from "@/enums/permissions-relation";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "MemberDialog",
  components: {
    PreviewData,
    TooltipIcon,
    PermissionItem,
    SectionLoader
  },
  mixins: [companyMember],
  props: {
    show: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    facilitiesIds() {
      return this.$store.state.permissions.member.facilitiesIds;
    },
    facilitiesGroupsIds() {
      return this.$store.state.permissions.member.facilitiesGroupsIds;
    },
    filteredGroups() {
      return this.groups
        .map(group => {
          return {
            ...group,
            children: group.children.filter(child =>
              "hasPermission" in child ? child.hasPermission : true
            )
          };
        })
        .filter(
          group =>
            ("hasPermission" in group ? group.hasPermission : true) &&
            group.children.length
        );
    }
  },
  data() {
    return {
      loading: false,
      groups: [
        {
          name: this.$t("message.permissionsData.group.name.basic"),
          rightLabel: true,
          children: [
            { name: "viewFacilitiesData", readonly: true },
            { name: "viewCompanyData", readonly: true },
            { name: "viewDashboard", readonly: true },
            { name: "manageGmbAccount", readonly: true },
            { name: "manageExternalGmbAddresses", readonly: true },
            { name: "viewTeam", readonly: true },
            { name: "manageTeam", readonly: true },
            { name: "manageCompanyData", readonly: true },
            { name: "protectFromExternalChanges", readonly: true },
            { name: "manageReviewsCategories", readonly: true },
            { name: "manageStoreLocator", readonly: true },
            {
              name: "configureAiReviewReply",
              readonly: true,
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_USE_AI_REVIEW_REPLY
              )
            }
          ]
        },
        {
          name: this.$t("message.permissionsData.group.name.gmbEdit"),
          hasPermission: planRestrictionsCheck(
            PLAN_RESTRICTIONS.CAN_MANAGE_LOCATION_DATA
          ),
          rightLabel: true,
          children: [
            { name: "editFacilityName" },
            { name: "editFacilityAddress" },
            { name: "editFacilityHours" },
            { name: "editFacilityWebsite" },
            { name: "editFacilityPhoneNumber" },
            { name: "editFacilityStoreCode" },
            { name: "editFacilityOpeningDate" },
            { name: "editFacilityDescription" },
            { name: "editFacilityAttributes" },
            { name: "editFacilityServiceItems" },
            { name: "editFacilityPrimaryCategory" },
            { name: "editFacilityAdditionalCategories" },
            { name: "editFacilityLabels" },
            { name: "editFacilityOpenStatus" }
          ]
        },
        {
          name: this.$t("message.permissionsData.group.name.externalChanges"),
          hasPermission: planRestrictionsCheck(
            PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
          ),
          children: [
            { name: "viewExternalChanges" },
            { name: "approveAndRejectExternalChanges" },
            { name: "viewExternalChangesHistory" }
          ]
        },
        {
          name: this.$t("message.permissionsData.group.name.gmbPosts"),
          hasPermission: planRestrictionsCheck(
            PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
          ),
          children: [
            { name: "viewLocalPosts" },
            { name: "addAndDeleteLocalPosts" }
          ]
        },
        {
          name: this.$t("message.permissionsData.group.name.photos"),
          hasPermission: planRestrictionsCheck(
            PLAN_RESTRICTIONS.CAN_POST_PHOTOS
          ),
          children: [{ name: "viewPhotos" }, { name: "addAndDeletePhotos" }]
        },
        {
          name: this.$t("message.permissionsData.group.name.gmbReviews"),
          hasPermission: planRestrictionsCheck(
            PLAN_RESTRICTIONS.CAN_MONITOR_RATINGS_AND_REPORTS
          ),
          children: [
            { name: "viewReviewsAnalytics" },
            { name: "viewReviews" },
            {
              name: "replyReviews",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_REPLY_TO_REVIEWS_MANUALLY
              )
            },
            {
              name: "assignReviewsCategories",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_USE_SENTIMENT_ANALYSIS
              )
            },
            {
              name: "editReviewReplyTemplates",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_USE_REVIEW_TEMPLATES
              )
            }
          ]
        },
        {
          name: this.$t("message.permissionsData.group.name.gmbAnalytics"),
          children: [
            {
              name: "viewGmbInsights",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_MONITOR_GMB_ACTIONS_AND_REPORTS
              )
            },
            {
              name: "viewMapsRankings",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
              )
            },
            {
              name: "viewMapsCompetitionRankings",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_COMPARE_COMPETITION
              )
            },
            {
              name: "manageCompetitionPlaces",
              hasPermission: planRestrictionsCheck(
                PLAN_RESTRICTIONS.CAN_COMPARE_COMPETITION
              )
            },
            {
              name: "manageKeywords"
            }
          ]
        },
        {
          name: this.$t(
            "message.permissionsData.group.name.questionsAndAnswers"
          ),
          hasPermission:
            planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_Q_AND_A) &&
            isFeatureFlagEnabled(FEATURE_FLAG.CN_6543_QUESTIONS_AND_ANSWERS),
          children: [
            {
              name: "viewQAndA",
              relatedPermission: [
                { name: "addAndEditQAndA", relation: RELATION.CHILD },
                { name: "deleteQAndA", relation: RELATION.CHILD }
              ]
            },
            {
              name: "addAndEditQAndA",
              relatedPermission: [
                { name: "viewQAndA", relation: RELATION.PARENT },
                { name: "deleteQAndA", relation: RELATION.CHILD }
              ]
            },
            {
              name: "deleteQAndA",
              relatedPermission: [
                {
                  name: "addAndEditQAndA",
                  relation: RELATION.PARENT
                },
                {
                  name: "viewQAndA",
                  relation: RELATION.PARENT
                }
              ]
            }
          ]
        },
        {
          name: this.$t("message.permissionsData.group.name.reviewBooster"),
          hasPermission: isFeatureFlagEnabled(FEATURE_FLAG.SHOW_REVIEW_BOOSTER),
          children: [
            {
              name: "viewRbForms",
              relatedPermission: [
                { name: "viewRbReviews", relation: RELATION.CHILD },
                { name: "viewRbReviewsAnalytics", relation: RELATION.CHILD }
              ]
            },
            {
              name: "viewRbReviews",
              relatedPermission: [
                { name: "viewRbForms", relation: RELATION.PARENT }
              ]
            },
            {
              name: "viewRbReviewsAnalytics",
              relatedPermission: [
                { name: "viewRbForms", relation: RELATION.PARENT }
              ],
              hasPermission: isFeatureFlagEnabled(
                FEATURE_FLAG.SHOW_RB_CATEGORIES_ANALYTICS
              )
            }
          ]
        }
      ],
      facilityPermissionsEnum: FacilityPermissionsEnum
    };
  },
  mounted() {
    if (this.facilitiesGroupsIds.length) {
      this.$store.commit(
        "permissions/SET_PERMISSION_TYPE",
        this.facilityPermissionsEnum.SELECT_GROUPS
      );
    } else if (this.facilitiesIds.length) {
      this.$store.commit(
        "permissions/SET_PERMISSION_TYPE",
        this.facilityPermissionsEnum.SELECTED
      );
    }
  },
  methods: {
    showCompleteDialog() {
      // GTM event data collection below
      const editablePermissionsNames = Object.keys(
        this.$store.state.permissions.member.permissions
      ).filter(
        key => !this.groups[0].children.map(child => child.name).includes(key)
      );
      const editablePermissionsGrantedNames = editablePermissionsNames.filter(
        permissionName =>
          this.$store.state.permissions.member.permissions[permissionName]
      );

      const gtmEventParams = {
        event: GTM_EVENT_NAME.PERMISSIONS_USER_ADD_LIMITED_ACCESS_SUMMARY,
        has_access_to_all_locations: this.$store.state.permissions.member
          .allFacilitiesAccess,
        num_permissions_granted: editablePermissionsGrantedNames
          ? editablePermissionsGrantedNames.length
          : null,
        num_all_permissions: editablePermissionsNames
          ? editablePermissionsNames.length
          : null
      };

      if (!this.$store.state.permissions.member.allFacilitiesAccess) {
        if (this.$store.state.permissions.member.facilitiesIds) {
          gtmEventParams.num_locations =
            this.$store.state.permissions.member.facilitiesIds.length || null;

          editablePermissionsGrantedNames.forEach(name => {
            this.$gtm.trackCustomEvent({
              event: GTM_EVENT_NAME.PERMISSIONS_USER_ADD_LIMITED_ACCESS_DETAILS,
              num_locations:
                this.$store.state.permissions.member.facilitiesIds.length ||
                null,
              permission_granted: snakeCase(name)
            });
          });
        }
      }

      this.$gtm.trackCustomEvent(gtmEventParams);
      // end GTM event stuff

      this.setActiveDialog(PermissionsActiveDialog.COMPLETE);
    },
    showFacilitiesDialog() {
      this.setActiveDialog(PermissionsActiveDialog.FACILITIES);
    },
    showEditPermissions() {
      this.setActiveDialog(PermissionsActiveDialog.PERMISSIONS, "edit");
    }
  }
};
</script>

<style lang="scss" scoped>
.permissions-list {
  .permissions-list-group {
    margin-top: $spacing * 3;
    padding-bottom: $spacing;

    .name {
      font-weight: 600;
      @include font-size(16px);
      line-height: 20px;
      color: $text;
      display: flex;
      align-items: center;
    }

    .right-label {
      text-align: right;
      font-weight: 500;
      @include font-size(14px);
      line-height: 17px;
      color: rgba($text, 0.4);
      margin-bottom: $spacing;
    }
  }
}
</style>
